import { createAsyncThunk } from '@reduxjs/toolkit';
import Endpoints from 'constants/endpoints';
import { HTTP_STATS_CODE } from 'constants/general';
import {
  ClassFocusSession,
  ClassSessionHeartbeatPayload,
  ClassSessionInvitation,
  SubmitClassUnlockRequestPayload
} from 'interfaces/class-session.interface';
import { toast } from 'react-toastify';
import focusBearApi from 'services/axios-config';
import { t } from 'services/i18n.service';
import { captureException } from 'services/posthog.service';
import {
  updateAreInvitationsLoading,
  updateCurrentSession,
  updateInvitations,
  updateIsCurrentSessionLoading,
  updateIsDeclining,
  updateIsJoining,
  updateIsLeaving,
  updateIsSubmittingUnlock,
  updateLastHeartbeatAt
} from './slice';

export const fetchClassSessionInvitations = createAsyncThunk(
  'class_session/fetch_invitations',
  async (_, { dispatch }) => {
    try {
      dispatch(updateAreInvitationsLoading(true));
      const { data } = await focusBearApi.get(
        Endpoints.CLASS_SESSION.INVITATIONS
      );
      const invitations = Array.isArray(data)
        ? (data as ClassSessionInvitation[])
        : [];
      dispatch(updateInvitations(invitations));
      return invitations;
    } catch (error) {
      captureException(error);
      throw error;
    } finally {
      dispatch(updateAreInvitationsLoading(false));
    }
  }
);

export const fetchCurrentClassSession = createAsyncThunk(
  'class_session/fetch_current',
  async (_, { dispatch }) => {
    try {
      dispatch(updateIsCurrentSessionLoading(true));
      const { data } = await focusBearApi.get(Endpoints.CLASS_SESSION.CURRENT);
      const currentSession: ClassFocusSession | null = data?.id ? data : null;
      dispatch(updateCurrentSession(currentSession));
      return currentSession;
    } catch (error) {
      captureException(error);
      throw error;
    } finally {
      dispatch(updateIsCurrentSessionLoading(false));
    }
  }
);

export const joinClassSession = createAsyncThunk(
  'class_session/join',
  async (sessionId: string, { dispatch }) => {
    if (!sessionId) {
      toast.error(t('could_not_process_the_request'));
      return;
    }
    try {
      dispatch(updateIsJoining(true));
      const { status, data } = await focusBearApi.post(
        Endpoints.CLASS_SESSION.JOIN(sessionId)
      );
      if ([HTTP_STATS_CODE.SUCCESS, HTTP_STATS_CODE.CREATED].includes(status)) {
        data?.id && dispatch(updateCurrentSession(data));
        dispatch(fetchClassSessionInvitations());
        return data as ClassFocusSession;
      }
      toast.error(t('could_not_process_the_request'));
    } catch (error) {
      toast.error(t('could_not_process_the_request'));
      captureException(error);
    } finally {
      dispatch(updateIsJoining(false));
    }
  }
);

export const declineClassSession = createAsyncThunk(
  'class_session/decline',
  async (sessionId: string, { dispatch }) => {
    if (!sessionId) {
      toast.error(t('could_not_process_the_request'));
      return false;
    }
    try {
      dispatch(updateIsDeclining(true));
      const { status } = await focusBearApi.post(
        Endpoints.CLASS_SESSION.DECLINE(sessionId)
      );
      if (
        [HTTP_STATS_CODE.SUCCESS, HTTP_STATS_CODE.NO_CONTENT].includes(status)
      ) {
        dispatch(fetchClassSessionInvitations());
        return true;
      }
      toast.error(t('could_not_process_the_request'));
      return false;
    } catch (error) {
      toast.error(t('could_not_process_the_request'));
      captureException(error);
      return false;
    } finally {
      dispatch(updateIsDeclining(false));
    }
  }
);

export const leaveClassSession = createAsyncThunk(
  'class_session/leave',
  async (sessionId: string, { dispatch }) => {
    try {
      dispatch(updateIsLeaving(true));
      const { status } = await focusBearApi.post(
        Endpoints.CLASS_SESSION.LEAVE(sessionId)
      );
      if (
        [HTTP_STATS_CODE.SUCCESS, HTTP_STATS_CODE.NO_CONTENT].includes(status)
      ) {
        dispatch(updateCurrentSession(null));
        dispatch(updateLastHeartbeatAt(null));
        return true;
      }
      toast.error(t('could_not_process_the_request'));
      return false;
    } catch (error) {
      toast.error(t('could_not_process_the_request'));
      captureException(error);
      return false;
    } finally {
      dispatch(updateIsLeaving(false));
    }
  }
);

export const submitClassUnlockRequest = createAsyncThunk(
  'class_session/submit_unlock_request',
  async (
    { session_id, ...body }: SubmitClassUnlockRequestPayload,
    { dispatch }
  ) => {
    try {
      dispatch(updateIsSubmittingUnlock(true));
      const { status, data } = await focusBearApi.post(
        Endpoints.CLASS_SESSION.UNLOCK_REQUESTS(session_id),
        body
      );
      if ([HTTP_STATS_CODE.SUCCESS, HTTP_STATS_CODE.CREATED].includes(status)) {
        return data;
      }
      toast.error(t('could_not_process_the_request'));
    } catch (error) {
      toast.error(t('could_not_process_the_request'));
      captureException(error);
    } finally {
      dispatch(updateIsSubmittingUnlock(false));
    }
  }
);

export const sendClassSessionHeartbeat = createAsyncThunk(
  'class_session/heartbeat',
  async (
    {
      sessionId,
      payload
    }: { sessionId: string; payload: ClassSessionHeartbeatPayload },
    { dispatch }
  ) => {
    try {
      const { status } = await focusBearApi.post(
        Endpoints.CLASS_SESSION.HEARTBEAT(sessionId),
        payload
      );
      if ([HTTP_STATS_CODE.SUCCESS, HTTP_STATS_CODE.CREATED].includes(status)) {
        dispatch(updateLastHeartbeatAt(new Date().toISOString()));
        return true;
      }
      return false;
    } catch (error) {
      captureException(error);
      return false;
    }
  }
);

export const completeClassSessionTask = createAsyncThunk(
  'class_session/complete_task',
  async ({ sessionId, taskId }: { sessionId: string; taskId: string }) => {
    try {
      const { status } = await focusBearApi.post(
        Endpoints.CLASS_SESSION.COMPLETE_TASK(sessionId, taskId)
      );
      return [HTTP_STATS_CODE.SUCCESS, HTTP_STATS_CODE.CREATED].includes(
        status
      );
    } catch (error) {
      captureException(error);
      return false;
    }
  }
);

export const uncompleteClassSessionTask = createAsyncThunk(
  'class_session/uncomplete_task',
  async ({ sessionId, taskId }: { sessionId: string; taskId: string }) => {
    try {
      const { status } = await focusBearApi.delete(
        Endpoints.CLASS_SESSION.COMPLETE_TASK(sessionId, taskId)
      );
      return [HTTP_STATS_CODE.SUCCESS, HTTP_STATS_CODE.NO_CONTENT].includes(
        status
      );
    } catch (error) {
      captureException(error);
      return false;
    }
  }
);
